import React, { useState, useEffect } from 'react';
import '../styles/Toolbar.css';

function Toolbar({ 
  isVisible, 
  rainActive, 
  windActive, 
  bgmActive, 
  isDistractedMode,
  imagesHidden,
  onRainToggle,
  onWindToggle,
  onBgmToggle,
  onDistractedToggle,
  onImagesToggle,
  onIncreaseFontSize,
  onDecreaseFontSize,
  onRainVolumeChange,
  onWindVolumeChange,
  onBgmVolumeChange
}) {
  // State to track if device is iOS
  const [isIOS, setIsIOS] = useState(false);
  
  // Detect iOS devices on component mount
  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod|macintosh/.test(userAgent) && 'ontouchend' in document;
    setIsIOS(isIOSDevice);
  }, []);
  
  // Handler for touch events on sliders - improved to not block scrolling
  const handleTouchVolumeChange = (handler) => (e) => {
    // Only prevent default if touch is directly on the slider
    // This allows scrolling to work normally elsewhere
    if (e.target.tagName === 'INPUT' && e.target.type === 'range') {
      e.preventDefault();
      
      // Get slider element and its dimensions
      const slider = e.target;
      const rect = slider.getBoundingClientRect();
      const width = rect.width;
      
      // Calculate touch position relative to slider (0-1)
      const touchX = e.touches[0].clientX - rect.left;
      const ratio = Math.max(0, Math.min(1, touchX / width));
      
      // Update the slider's value visually
      slider.value = ratio;
      
      // Create a properly structured synthetic event
      const syntheticEvent = {
        target: {
          value: ratio.toString()
        }
      };
      
      // Call the handler with our synthetic event
      handler(syntheticEvent);
      
      // Force immediate audio update for instant feedback
      if (handler === onRainVolumeChange && rainActive) {
        // Access audio element directly through DOM as fallback
        const rainAudio = document.getElementById('rainSound');
        if (rainAudio) rainAudio.volume = ratio;
      } else if (handler === onWindVolumeChange && windActive) {
        const windAudio = document.getElementById('windSound');
        if (windAudio) windAudio.volume = ratio;
      } else if (handler === onBgmVolumeChange && bgmActive) {
        const bgmAudio = document.getElementById('bgmSound');
        if (bgmAudio) bgmAudio.volume = ratio;
      }
    }
  };
  
  return (
    <div className={`toolbar ${!isVisible ? 'hidden' : ''}`}>
      <div className="toolbar-controls">
        <div className="toolbar-row">
          <div className="rain-control">
            <button 
              id="rainToggle" 
              className={`toggle-btn ${rainActive ? 'active' : ''}`}
              onClick={onRainToggle}
            >
              Rain: {rainActive ? 'On' : 'Off'}
            </button>
            {!isIOS && rainActive && (
              <input 
                type="range" 
                id="rainVolume" 
                min="0" 
                max="1" 
                step="0.01" 
                defaultValue="0.7" 
                className="volume-slider"
                onChange={onRainVolumeChange}
                onTouchStart={handleTouchVolumeChange(onRainVolumeChange)}
                onTouchMove={handleTouchVolumeChange(onRainVolumeChange)}
              />
            )}
            {isIOS && rainActive && (
              <div className="ios-volume-notice">Volume control unavailable on iOS</div>
            )}
          </div>
          
          <div className="wind-control">
            <button 
              id="windToggle" 
              className={`toggle-btn ${windActive ? 'active' : ''}`}
              onClick={onWindToggle}
            >
              Wind: {windActive ? 'On' : 'Off'}
            </button>
            {!isIOS && windActive && (
              <input 
                type="range" 
                id="windVolume" 
                min="0" 
                max="1" 
                step="0.01" 
                defaultValue="0.7" 
                className="volume-slider"
                onChange={onWindVolumeChange}
                onTouchStart={handleTouchVolumeChange(onWindVolumeChange)}
                onTouchMove={handleTouchVolumeChange(onWindVolumeChange)}
              />
            )}
            {isIOS && windActive && (
              <div className="ios-volume-notice">Volume control unavailable on iOS</div>
            )}
          </div>
          
          <div className="music-control">
            <button 
              id="bgmToggle" 
              className={`toggle-btn ${bgmActive ? 'active' : ''}`}
              onClick={onBgmToggle}
            >
              Music: {bgmActive ? 'On' : 'Off'}
            </button>
            {!isIOS && bgmActive && (
              <input 
                type="range" 
                id="bgmVolume" 
                min="0" 
                max="1" 
                step="0.01" 
                defaultValue="0.7" 
                className="volume-slider"
                onChange={onBgmVolumeChange}
                onTouchStart={handleTouchVolumeChange(onBgmVolumeChange)}
                onTouchMove={handleTouchVolumeChange(onBgmVolumeChange)}
              />
            )}
            {isIOS && bgmActive && (
              <div className="ios-volume-notice">Volume control unavailable on iOS</div>
            )}
          </div>
        </div>
        
        <div className="toolbar-row">
          <div className="distracted-control">
            <button 
              id="distractedToggle" 
              className={`toggle-btn ${isDistractedMode ? 'active' : ''}`}
              onClick={onDistractedToggle}
            >
              Distracted: {isDistractedMode ? 'Yes' : 'No'}
            </button>
          </div>
          
          <div className="font-controls">
            <div className="font-btns-container">
              <button id="decreaseFont" className="font-btn" onClick={onDecreaseFontSize}>A-</button>
              <button id="increaseFont" className="font-btn" onClick={onIncreaseFontSize}>A+</button>
            </div>
          </div>
          
          <div className="images-control">
            <button 
              id="imagesToggle" 
              className={`toggle-btn ${imagesHidden ? 'active' : ''}`}
              onClick={onImagesToggle}
            >
              Images: {imagesHidden ? 'Off' : 'On'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Toolbar; 